import type { InvoicePrintTemplateId } from '@helios/data-access';

/**
 * System invoice print templates available in v1 (PRD.238 §6.1).
 *
 * Each template defines high-level layout strategy hints that the
 * generated `InvoicePrintLayout` renderer applies on top of the clinic's
 * branding + section settings.
 */

export type HeaderAlign = 'left' | 'center' | 'right';
export type Density = 'compact' | 'comfortable';
export type TotalsStyle = 'right_block' | 'full_width' | 'minimal';
export type AccentStyle = 'filled' | 'underline' | 'plain';

export interface InvoiceSystemTemplate {
  id: InvoicePrintTemplateId;
  label: string;
  description: string;

  headerAlign: HeaderAlign;
  density: Density;
  prominentLogo: boolean;
  /** Show a centered "INVOICE" heading band over the items table. */
  showInvoiceHeading: boolean;
  /** Whether the items table header uses the brand accent fill. */
  accentStyle: AccentStyle;
  /** Layout for the subtotal/discount/tax/balance block. */
  totalsStyle: TotalsStyle;
}

export const INVOICE_SYSTEM_TEMPLATES: InvoiceSystemTemplate[] = [
  {
    id: 'classic_billing',
    label: 'Classic Billing',
    description:
      'Branded letterhead with clinic on the left, invoice meta on the right, themed items table, and a clean totals block.',
    headerAlign: 'left',
    density: 'comfortable',
    prominentLogo: true,
    showInvoiceHeading: true,
    accentStyle: 'filled',
    totalsStyle: 'right_block',
  },
  {
    id: 'clean_classic',
    label: 'Clean Classic',
    description:
      'Centered logo + clinic header with a neutral items table — works well for general billing.',
    headerAlign: 'center',
    density: 'comfortable',
    prominentLogo: true,
    showInvoiceHeading: true,
    accentStyle: 'underline',
    totalsStyle: 'right_block',
  },
  {
    id: 'compact_modern',
    label: 'Compact Modern',
    description:
      'Left-aligned modern header. Dense items table — fits more rows per page; good for long bills.',
    headerAlign: 'left',
    density: 'compact',
    prominentLogo: false,
    showInvoiceHeading: false,
    accentStyle: 'underline',
    totalsStyle: 'right_block',
  },
  {
    id: 'statement_invoice',
    label: 'Statement Invoice',
    description:
      'Statement-style layout — clear invoice header strip, prominent balance due, payment history section if enabled.',
    headerAlign: 'left',
    density: 'comfortable',
    prominentLogo: true,
    showInvoiceHeading: true,
    accentStyle: 'filled',
    totalsStyle: 'full_width',
  },
  {
    id: 'minimal_billing',
    label: 'Minimal Billing',
    description:
      'Stripped-back layout for simple receipts. No themed header, minimal chrome, totals only at the bottom right.',
    headerAlign: 'left',
    density: 'compact',
    prominentLogo: false,
    showInvoiceHeading: false,
    accentStyle: 'plain',
    totalsStyle: 'minimal',
  },
  {
    // PRD.284 — Letterhead Classic. Renders via a dedicated layout branch in
    // InvoicePrintLayout (two-column doctor/practice header + centered footer)
    // for clinics using traditional pre-printed stationery. The hint fields
    // below are kept sensible so any generic code paths still behave.
    id: 'classic_letterhead',
    label: 'Letterhead Classic',
    description:
      'Traditional two-column doctor header with centered footer for pre-printed clinic stationery.',
    headerAlign: 'left',
    density: 'comfortable',
    prominentLogo: true,
    showInvoiceHeading: true,
    accentStyle: 'plain',
    totalsStyle: 'right_block',
  },
];

export function getInvoiceSystemTemplate(id: InvoicePrintTemplateId): InvoiceSystemTemplate {
  return INVOICE_SYSTEM_TEMPLATES.find((t) => t.id === id) ?? INVOICE_SYSTEM_TEMPLATES[0];
}
